import * as React from 'react';
import { Link } from 'react-router-dom';

import { HiExclamationCircle, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { RegistrationService } from 'src/registration/service/registration.service';
import { PaymentMethodManager } from 'src/registration/ui/components/payment-method-manager';
import { RegistrationActions } from 'src/registration/ui/components/registration-actions';
import { StatusChip } from 'src/shared/index';
import { LinkButton, Table } from 'src/shared/ui/elements';
import YourProgramsMobile from './your-programs-mobile';
import { EventService } from 'src/event/service/event.service';

const EventAddress = ({ event, registrationEventName }) => {
  const timeLimits = EventService.timeLabel(event);
  const dateLimits = EventService.dateLimits(event);
  const datesLabel = `${EventService.dateLabel(event)}${
    dateLimits ? ` (${dateLimits})` : ''
  }`;
  return (
    <div>
      <div>
        <div className="fw-500">
          {registrationEventName || event?.effectiveName || event?.label}
        </div>
        <div className="fw-500">{event?.program?.name}</div>
        <div>
          <div className="fs-12 fc-gray-55 mt-4">{event?.location?.name}</div>

          <div className="fs-10 fc-gray-55 mt-4">{timeLimits}</div>
          <div className="fs-10 fc-gray-55 mt-4">{datesLabel}</div>
        </div>
      </div>
    </div>
  );
};
const YourProgramsDesktopItem = ({ registration }) => {
  const { event } = registration;
  const noGuardians = registration.guardians.length === 0;
  return (
    <tr
      className={noGuardians ? '[&>td]:tw-bg-red [&>td]:tw-bg-opacity-20' : ''}
    >
      <td className="fw-600">
        <Link to={`/registrations/${registration.id}`}>{registration.id}</Link>
      </td>
      <td>
        {registration.participant?.fullName || '-'}
        {registration.status === 'WITHDRAWN' && (
          <span className="fc-accent-50 block mt-10 fw-600">Withdrawn</span>
        )}
        {registration.status === 'PAUSED' && (
          <span className="fc-accent-50 block mt-10 fw-600">Paused</span>
        )}
        {registration.status === 'WAITLISTED' && (
          <StatusChip $level="warning">On Waitlist</StatusChip>
        )}
      </td>
      <td className="fw-500">
        {noGuardians ? (
          <div className="tw-text-red">No guardians selected</div>
        ) : (
          <div className="grid program-guardians">
            {registration.guardians.map((guardian, index) => {
              const inactive = guardian.type ? 'false' : 'true';
              return (
                <React.Fragment key={`${guardian.id}--${index}`}>
                  <span data-inactive={inactive}>{guardian.fullName}</span>
                  <span
                    data-inactive={inactive}
                    className="css-element--pill bg-primary-45"
                  >
                    {guardian.type ?? 'Inactive'}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </td>
      <td>
        <EventAddress
          event={event}
          registrationEventName={registration.registrationEventName}
        />
      </td>
      <td className="fw-600">
        <PaymentMethodManager
          $registration={registration}
          $buttonClassName="text mt-4 fs-12"
        />
      </td>
      <td className="fw-600">
        {registration.cancelRegistrationRequest?.status !== 'WITHDRAWN' && (
          <RegistrationActions $registration={registration} />
        )}
        {registration.cancelRegistrationRequest && (
          <div className="fs-12 fc-gray-55 mt-10">
            {RegistrationService.getCancelRequestValue(
              registration.cancelRegistrationRequest,
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
const YourProgramsDesktop = ({
  $item: registration,
  $upcomingEventsMapByRegistrationId,
}) => {
  const upcomingEvents = $upcomingEventsMapByRegistrationId?.[registration.id];

  return (
    <>
      <YourProgramsDesktopItem registration={registration} />
      {upcomingEvents?.length > 0 && (
        <tr className="tw-bg-[#e6f7e6]">
          <td colSpan={3} className="tw-border-y-[#30cd92] tw-border-b-[1px]">
            <div className="flex tw-gap-2 tw-text-[#01c786] tw-text-sm tw-items-center tw-font-bold">
              <HiExclamationCircle />
              <span>
                Sign {registration?.participant?.firstName} up for the next
                season!
              </span>
            </div>
          </td>
          <td colSpan={3} className="tw-border-y-[#30cd92] tw-border-b-[1px]">
            <div className="flex tw-flex-col tw-gap-3">
              {upcomingEvents?.map((item) => (
                <div key={item.id} className="tw-grid tw-grid-cols-4 tw-gap-3">
                  <div className="tw-col-span-2">
                    <EventAddress
                      event={item}
                      registrationEventName={registration.registrationEventName}
                    />
                  </div>

                  <div className="tw-min-w-[140px] tw-text-center">
                    <Link
                      target="_blank"
                      className="tw-font-semibold tw-text-xs"
                      to={`/events/spotlight/${item.code}`}
                    >
                      View Details
                    </Link>
                  </div>
                  <LinkButton
                    className="!tw-bg-[#03c786] !tw-border-[#30cd92] tw-max-w-28 tw-justify-self-end"
                    to={`/events/${item.code}`}
                    target="_blank"
                  >
                    <div className="flex tw-items-center tw-gap-2 tw-justify-center">
                      <span>Enroll</span>
                      <HiOutlineArrowNarrowRight />
                    </div>
                  </LinkButton>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const YourPrograms = ({
  $registrations,
  $upcomingEventsMapByRegistrationId = {},
}) => {
  const registrations = React.useMemo(
    () =>
      RegistrationService.getRegistrationsByStatus($registrations, [
        'ACTIVE',
        'WITHDRAWN',
        'PAUSED',
      ]),
    [$registrations],
  );

  return (
    <Table
      $caption="Your Programs"
      $headers={[
        'ID',
        'Name',
        'Parent/Guardian',
        'Program Details',
        'Payment Method',
        'Action',
      ]}
      $items={registrations}
      $desktopItem={YourProgramsDesktop}
      $mobileItem={YourProgramsMobile}
      $emptyMessage="You have no active registrations yet"
      $extraProps={{
        $upcomingEventsMapByRegistrationId,
      }}
    />
  );
};
